import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import isEqual from 'lodash/isEqual';

import { withSearchHandler, withModalStateHandlers } from 'decorators';
import { ModalClose } from 'components/core/Modal';
import { Button } from 'components/core/Button';
import { Section } from 'components/core/Page';
import { StaticMap } from 'components/core/StaticMap';
import { SearchBox } from 'components/search/SearchBox';
import { StickySearchBar } from 'components/landing/StickySearchBar';
import { Search } from 'components/svg';

import './SearchRail.scss';

const withAdditionalSearchParams = (WrappedComponent) => (props) => (
	<WrappedComponent
		{...props}
		additionalSearchParams={{ informedSearch: true }}
	/>
);

const withSearchRailLogic = (WrappedComponent) => (props) => {
	const [filterParams, setFilterParams] = useState(props.queryParams);

	const handleQueryParamChange = useCallback(
		(params) => {
			if (props.isLarge) {
				props.onSearchParamChange(params);
			}
			setFilterParams(params);
		},
		[props.isLarge, props.onSearchParamChange],
	);

	const handleApplyFilters = useCallback(() => {
		props.onSearchParamChange(filterParams);
		const element = document.querySelector('.serp-container');
		if (element) {
			window.scrollTo({
				top: element.offsetTop - 130,
				behavior: 'smooth',
			});
		}
	}, [props.onSearchParamChange, filterParams]);

	const prevQueryParamsRef = useRef(props.queryParams);
	useEffect(() => {
		if (!isEqual(prevQueryParamsRef.current, props.queryParams)) {
			prevQueryParamsRef.current = props.queryParams;
			setFilterParams(props.queryParams);
		}
	});

	return (
		<WrappedComponent
			{...props}
			filterParams={filterParams}
			handleQueryParamChange={handleQueryParamChange}
			handleApplyFilters={handleApplyFilters}
		/>
	);
};

export const enhance = (Component) =>
	withAdditionalSearchParams(
		withSearchHandler(
			withModalStateHandlers(withSearchRailLogic(Component)),
		),
	);

export function SearchRail({
	openInteractiveMap,
	children,
	filtersShowing,
	closeFilters,
	handleQueryParamChange,
	filterParams,
	handleApplyFilters,
	handleSearch,
	informedSearch,
}) {
	return (
		<div className={classnames('search-rail', { showing: filtersShowing })}>
			<div className="rail-content">
				{informedSearch && (
					<Section className="rail-search">
						<SearchBox
							inputLabel="Refine your search"
							handleSearch={handleSearch}
							id="informed-search-box"
							buttonIcon={true}
							buttonText=""
						/>
						<StickySearchBar
							triggerId="informed-search-box"
							handleSearch={handleSearch}
						/>
					</Section>
				)}
				{filtersShowing && (
					<ModalClose
						onClick={closeFilters}
						className="close-button"
					/>
				)}

				<StaticMap className="rail-static-map">
					<Button
						className=" view-map"
						onClick={openInteractiveMap}
					>
						Compare prices on map
					</Button>
				</StaticMap>

				{React.cloneElement(children, {
					queryParams: filterParams,
					onFilter: handleQueryParamChange,
				})}
			</div>
			<div className={classnames('apply-all-filters')}>
				<Button onClick={handleApplyFilters}>Apply</Button>
			</div>
		</div>
	);
}

SearchRail.propTypes = {
	filtersShowing: PropTypes.bool,
	openInteractiveMap: PropTypes.func.isRequired,
	children: PropTypes.node.isRequired,
	closeFilters: PropTypes.func,
	filterParams: PropTypes.object,
	handleQueryParamChange: PropTypes.func,
	handleApplyFilters: PropTypes.func.isRequired,
	handleSearch: PropTypes.func.isRequired,
	informedSearch: PropTypes.bool,
};

export default enhance(SearchRail);
